import type { LevelConfig, LevelLayer, LevelPiece, LevelPieceType } from "../level_config.js";

// Same numeric value as PlayDialogDifficulty.Common — kept as a literal
// instead of importing play_dialog.ts, same leaf-module reasoning as the
// other showcases' local COMMON_DIFFICULTY.
const COMMON_DIFFICULTY = 0;

/**
 * A tower that gets WIDER towards the top while standing on a tabletop only as
 * wide as its bottom pair, built the Jenga way: every block lies flat on its
 * broad face and only its heading changes, so nothing in here balances on an
 * edge.
 *
 * Three headings, all rotationDegY (yaw — the block stays flat, it only turns
 * in plan):
 *
 *   - "flat" (0 deg)  — a plank spanning the middle. Physically `width` cells
 *                       across, 1 cell deep.
 *   - "post" (90 deg) — the same 2-cell bar turned to run into the screen, so
 *                       it covers ONE cell across and 2 deep. These are the
 *                       spacers the next row stands on.
 *   - "diag" (45 deg) — a bar turned corner-on. 2.14 cells across instead of
 *                       2.02, so it barely reaches further than a flat one;
 *                       it is the look, not the reach.
 *
 * The widening therefore comes from offsetX, not from the rotation: each cycle
 * the posts step outboard, and the plank between them grows to keep bridging
 * the pair below. Two rules keep it standing, and both are checked by hand in
 * LAYOUT's comments:
 *
 *   1. every row is symmetric about the tower axis, so the structure never
 *      leans as a whole;
 *   2. each overhanging piece keeps its centre of mass inboard of the outer
 *      edge of whatever carries it — the corbel rule. Margins here are ~0.35
 *      of a cell, which survives the ~2% settle every stack does on spawn.
 *
 * ONE LAYER ONLY, deliberately. A yawed 2-wide bar is 2.14 cells deep while
 * the layer pitch is 1.02, so a second depth layer would spawn half inside
 * these blocks and blow the tower apart on the first step.
 */

// Tabletop width is not a level field: field_layout sizes the top off the
// length of grid row 0, and createLayeredBlocks drops any piece whose `x` is
// past that same count. So the grid is kept down to the bottom pair's width
// and every block's real position is carried in offsetX instead — which is
// what places it anyway (position = x + (width - 1) / 2 + offsetX).
const COLS = 5;
// Tower axis, in column units. Every row is mirrored about it. Rows wider than
// the grid put their outer pieces at negative or past-the-end centres; that is
// fine, and it is exactly the overhang we are after.
const AXIS = 2;
// Purple plywood for every piece: one mass and one friction throughout, so a
// collapse is the geometry's fault and not a random skin's.
const VARIANT = 0;

type Kind = "flat" | "post" | "diag";

const YAW: Record<Kind, number | undefined> = {
  flat: undefined,
  post: 90,
  diag: 45,
};

interface RowPiece
{
  kind: Kind;
  width: number;
  // Centre of the piece as a distance from AXIS. Pairs are written once and
  // mirrored, so a row cannot go asymmetric by a typo.
  offset: number;
}

/** Half a piece's span across, in column units, before any yaw is applied. */
function halfWidth( width: number ): number
{
  return ( width + ( width - 1 ) * 0.02 ) / 2 / 1.02;
}

// Breathing room between a post and the plank it stands beside. Just enough
// that they do not spawn touching and get pushed apart on the first step.
const FLUSH_CLEARANCE = 0.03;

/**
 * Where a row's posts go so they sit flush against its plank, rather than
 * leaving the gap a hand-picked offset used to. Every widening step in the
 * tower comes out of this: the posts define where the diag row above them is
 * centred, so a wider plank pushes the whole cycle outboard.
 */
function postBeside( plank_width: number ): number
{
  return halfWidth( plank_width ) + halfWidth( 2 ) / 2 + FLUSH_CLEARANCE;
}

// Bottom row first. Physical spans, in column units, worked out for a 2-wide
// bar of 1.98 flat / 2.10 yawed 45 / 0.98 yawed 90:
//
// Four post rows, their planks stepping 2 - 3 - 4 - 5, with a diag pair under
// each. Every offset below falls out of postBeside(), so the only numbers
// authored by hand are the plank widths and the bottom pair.
//
//   row  pieces                     half-width   rests on
//   ---  -------------------------  -----------  --------------------------
//   0    diag pair  +/-1.10         2.15         the tabletop
//   1    post +/-1.52, plank w2     2.01         row 0, plank bridges the pair
//   2    diag pair  +/-1.52         2.57         row 1 post, centred + plank
//   3    post +/-2.02, plank w3     2.51         row 2, post steps 0.50 out
//   4    diag pair  +/-2.02         3.07         row 3 post, centred + plank
//   5    post +/-2.52, plank w4     3.01         row 4, post steps 0.50 out
//   6    diag pair  +/-2.52         3.57         row 5 post, centred + plank
//   7    post +/-3.02, plank w5     3.51         row 6, post steps 0.50 out
//
// Half-width runs 2.15 -> 3.57 against a tabletop half-width of about 2.41. The
// post rows read narrower than the diag rows under them because a post is
// narrower than the diag it stands on; that is the zigzag in the silhouette,
// not a mistake. The bottom pair cannot go below +/-1.05 without the two diags
// overlapping each other, which is why row 1 comes out a touch narrower than
// row 0.
//
// The top row is the one to watch. A diag yawed 45 is a DIAMOND in plan, so
// half a cell outboard of its centre the silhouette still under a post is only
// z -0.20..0.92 — off-centre against the post's own centre of mass at z 0, and
// by 0.7 out it is z 0.01..0.72, past it entirely. Every row but the last gets
// away with the step because the diag row above pins it down. The last one is
// held only by friction against the plank it now sits flush with, which is why
// FLUSH_CLEARANCE matters more than it looks: at the 0.12 gap this row used to
// have, its posts tipped 15 degrees and slid z +0.53 and +0.71 inside a hundred
// frames.
const LAYOUT: RowPiece[][] = [
  [ { kind: "diag", width: 2, offset: 1.10 } ],
  [ { kind: "post", width: 2, offset: postBeside( 2 ) }, { kind: "flat", width: 2, offset: 0 } ],
  [ { kind: "diag", width: 2, offset: postBeside( 2 ) } ],
  [ { kind: "post", width: 2, offset: postBeside( 3 ) }, { kind: "flat", width: 3, offset: 0 } ],
  [ { kind: "diag", width: 2, offset: postBeside( 3 ) } ],
  [ { kind: "post", width: 2, offset: postBeside( 4 ) }, { kind: "flat", width: 4, offset: 0 } ],
  [ { kind: "diag", width: 2, offset: postBeside( 4 ) } ],
  [ { kind: "post", width: 2, offset: postBeside( 5 ) }, { kind: "flat", width: 5, offset: 0 } ],
];

// Widest block this level may author. PhysicsCube builds anything past 3 cells
// by stretching the authored 1x3 FBX by blocks / 3
// (PhysicsCube#prepareAuthoredLongGeometry), and that is sized for 4/3 and 5/3
// — one or two extra neutral sections. A w8 plank comes out of it with a world
// bounding box of 260 x 33 x 32 instead of 5.2 x 0.64 x 0.64, and the body it
// feeds is thrown clear of the level on the first step: measured, it ended up
// at y 12 with the rest of the tower topping out at 3.8. Nothing warns about
// it, so the ceiling is asserted here instead.
const WIDTH_CEILING = 5;

const ROWS = LAYOUT.length;

interface Placed
{
  width: number;
  centre: number;
  yaw?: number;
}

/**
 * Expands a row's mirrored shorthand into its actual pieces, left to right.
 *
 * A pair is a true mirror image, so the left member's yaw is negated: a diag on
 * the right leans one way at +45 and its partner leans the other at -45. For a
 * post the sign changes nothing physically — a 2-cell bar yawed +90 and -90 is
 * the same box — but it keeps the rule "left is the mirror of right" with no
 * exceptions to remember.
 */
function expandRow( row: RowPiece[] ): Placed[]
{
  const out: Placed[] = [];

  for ( const piece of row )
  {
    const yaw = YAW[piece.kind];

    if ( piece.offset === 0 )
      out.push( { width: piece.width, centre: AXIS, yaw } );
    else
    {
      out.push( {
        width: piece.width,
        centre: AXIS - piece.offset,
        yaw: yaw === undefined ? undefined : -yaw,
      } );
      out.push( { width: piece.width, centre: AXIS + piece.offset, yaw } );
    }
  }

  return out.sort( ( a, b ) => a.centre - b.centre );
}

/**
 * Picks one starting grid cell per piece, left to right, as near each piece's
 * real centre as the room left for the pieces after it allows.
 *
 * The grid is narrower than the row it describes, so a piece's cell run can
 * come out shorter than the piece. That costs nothing structurally — the cells
 * only feed the splash-damage neighbour lookup, and the column it records
 * already folds offsetX back in (game_screen #block_grid_placement), so a short
 * run still lands on the piece. What must not happen is a piece ending up with
 * NO cells, which would drop it out of splash entirely, so every piece is given
 * a distinct cell of its own before any of them is allowed to grow.
 */
function assignStarts( row: Placed[] ): number[]
{
  const starts: number[] = [];

  row.forEach( ( piece, index ) =>
  {
    const lowest = index === 0 ? 0 : starts[index - 1] + 1;
    const highest = COLS - ( row.length - index );

    starts.push( Math.min( Math.max( Math.round( piece.centre ), lowest ), highest ) );
  } );

  return starts;
}

function makeDeck(): LevelLayer
{
  const grid: number[][] = [];
  const pieces: LevelPiece[] = [];
  let id = 1;

  // LAYOUT is written bottom-up; the grid's row 0 is the TOP of the wall,
  // because game_screen derives the height index as rows - y - ...
  for ( let row_index = ROWS - 1; row_index >= 0; row_index-- )
  {
    const y = ROWS - 1 - row_index;
    const row = expandRow( LAYOUT[row_index] );
    const starts = assignStarts( row );
    const grid_row: number[] = Array( COLS ).fill( 0 );

    row.forEach( ( piece, index ) =>
    {
      if ( piece.width > WIDTH_CEILING )
      {
        console.warn(
          `[TableCaseShowcase] row ${row_index}: width ${piece.width} is past the `
          + `${WIDTH_CEILING}-cell ceiling the block art supports — piece skipped`
        );
        return;
      }

      const start = starts[index];
      // Grow the run rightwards, stopping at the grid's end or at the cell the
      // next piece has already reserved for itself.
      const limit = index + 1 < row.length ? starts[index + 1] : COLS;

      for ( let col = start; col < Math.min( start + piece.width, limit, COLS ); col++ )
        grid_row[col] = id;

      pieces.push( {
        id,
        type: 2 as LevelPieceType,
        x: start,
        y,
        width: piece.width,
        height: 1,
        variant: VARIANT,
        // The cell run above is bookkeeping; this offset is what actually
        // places the block, so it is measured against the claimed start.
        offsetX: piece.centre - ( piece.width - 1 ) / 2 - start,
        rotationDegY: piece.yaw,
      } );

      id++;
    } );

    grid.push( grid_row );
  }

  return { grid, pieces };
}

const TABLE_CASE_LEVEL: LevelConfig = {
  level: 90308,
  difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
  tables: [
    // Half as wide as the blocks ask for, so the stack visibly overhangs the top
    // it stands on — see TableConfig.topScale. The depth is written out too but
    // does nothing here: this level is one layer deep, so its content depth is
    // 0.470 and PhysicsTable#stretchTableTopDepth floors the top at leg_depth
    // * 0.7 = 0.624 regardless. Measured, depth 3 does move it, to 1.418.
    { layers: [ makeDeck() ], slot: { u: 0, v: 0 }, topScale: { width: 0.5, depth: 0.7 } },
  ],
};

export class TableCaseShowcase
{
  public static readonly LEVEL = TABLE_CASE_LEVEL.level;

  private static active = false;

  public static isActive(): boolean
  {
    return this.active;
  }

  public static activate(): number
  {
    this.active = true;
    return this.LEVEL;
  }

  public static deactivate(): void
  {
    this.active = false;
  }

  public static getConfig( level: number ): LevelConfig | null
  {
    return level === this.LEVEL ? TABLE_CASE_LEVEL : null;
  }
}
